import React, { useEffect, useState, createContext, useContext } from 'react';
import { translations, Language } from './translations';
type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => any;
};
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export function LanguageProvider({
  children
}: {
  children: ReactNode;
}) {
  const [language, setLanguageState] = useState<Language>('en');
  useEffect(() => {
    const path = window.location.pathname;
    const pathParts = path.split('/');
    const langPrefix = pathParts[1] as Language;
    // Only ru, ua, ka use URL prefixes. English is the default at root.
    if (['ua', 'ru', 'ka'].includes(langPrefix)) {
      setLanguageState(langPrefix as Language);
    } else {
      // No valid language prefix means English (default)
      setLanguageState('en');
    }
  }, []);
  const setLanguage = (lang: Language) => {
    const path = window.location.pathname;
    const pathParts = path.split('/').filter(Boolean);
    // Determine current page slug (strip current language prefix if present)
    let pageSlug = '';
    const currentFirstPart = pathParts[0];
    if (['ru', 'ua', 'ka'].includes(currentFirstPart)) {
      // Currently on a non-EN language, page slug is pathParts[1]
      pageSlug = pathParts[1] || '';
    } else {
      // Currently on EN (no prefix), page slug is pathParts[0]
      pageSlug = currentFirstPart || '';
    }
    // Build new path
    let newPath: string;
    if (lang === 'en') {
      // English: no prefix
      newPath = pageSlug ? `/${pageSlug}` : '/';
    } else {
      // Other languages: with prefix
      newPath = pageSlug ? `/${lang}/${pageSlug}` : `/${lang}/`;
    }
    window.history.pushState(null, '', newPath);
    setLanguageState(lang);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };
  const t = (key: string) => {
    const keys = key.split('.');
    let value: any = translations[language];
    for (const k of keys) {
      if (value && value[k]) {
        value = value[k];
      } else {
        // Fallback to English if key missing
        let fallback: any = translations['en'];
        for (const fk of keys) {
          if (fallback && fallback[fk]) {
            fallback = fallback[fk];
          } else {
            return key; // Return key if not found even in English
          }
        }
        return fallback;
      }
    }
    return value;
  };
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t
  }} data-id="element-286">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}